import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { useToast } from '../hooks/use-toast';
import { MapPin, Heart, Users, Mail, Globe, Menu, X, ShoppingBag, Sparkles, Award, Package, HandHeart, Bike, DollarSign, FileSignature, ExternalLink } from 'lucide-react';
import MerchandiseCard from '../components/MerchandiseCard';
import CheckoutModal from '../components/CheckoutModal';
import FunJourneyMap from '../components/FunJourneyMap';

const Home = () => {
  const { toast } = useToast();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isCheckoutOpen, setIsCheckoutOpen] = useState(false);
  const [cartItems, setCartItems] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });

  const merchandiseItems = [
  {
    id: 1,
    type: 'tshirt',
    name: 'Peace Tour T-Shirt',
    description: 'Buy a T-Shirt to participate in our peace mission. High-quality cotton with exclusive dove design.',
    price: 35,
    sizes: 'XS - 4XL',
    image: 'https://customer-assets.emergentagent.com/job_peacetour-europe/artifacts/co99fyaz_image.png',
    badge: 'Popular',
    shippingNote: 'Make a donation of €35 to PayPal, leave your size, name and address, and you will get it by mail'
  },
  {
    id: 2,
    type: 'sticker',
    name: 'Peace Dove Sticker',
    description: 'Support us with a donation of €3. Beautiful peace dove sticker with EU stars design.',
    price: 3,
    image: 'https://customer-assets.emergentagent.com/job_peacetour-europe/artifacts/82zhwmxy_image.png',
    badge: 'Best Value',
    shippingNote: 'Order via PayPal, comment "Sticker", add your name and address and you will get it into your letterbox'
  }];


  const handleSubmit = (e) => {
    e.preventDefault();
    toast({
      title: "Message Sent!",
      description: "Thank you for your interest. We'll get back to you soon."
    });
    setFormData({ name: '', email: '', message: '' });
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    element?.scrollIntoView({ behavior: 'smooth' });
    setIsMenuOpen(false);
  };

  const handleAddToCart = (item) => {
    setCartItems([...cartItems, item]);
    setIsCheckoutOpen(true);
  };

  const handleCheckout = (checkoutData) => {
    const orderDetails = {
      ...checkoutData,
      items: cartItems,
      total: cartItems.reduce((sum, item) => sum + item.price, 0)
    };

    // Store order details in localStorage for reference
    localStorage.setItem('pendingOrder', JSON.stringify(orderDetails));

    // Prepare PayPal payment details
    const amount = orderDetails.total;
    const itemDetails = cartItems.map((item) =>
    `${item.name}${item.type === 'tshirt' ? ` (Size: ${checkoutData.size})` : ''}`
    ).join(', ');

    const shippingInfo = `${checkoutData.name}, ${checkoutData.address}, ${checkoutData.city}, ${checkoutData.postalCode}, ${checkoutData.country}`;

    // Create PayPal.me URL with amount
    const paypalUrl = `https://www.paypal.me/EuropeanPeaceTours/${amount}`;

    // Show confirmation toast
    toast({
      title: "Redirecting to PayPal...",
      description: `You'll be redirected to PayPal to complete your €${amount} donation.`,
      duration: 3000
    });

    // Show important instructions toast
    setTimeout(() => {
      toast({
        title: "Important: Add Order Details",
        description: `Please include in PayPal notes: "${itemDetails}" and your shipping address: "${shippingInfo}"`,
        duration: 10000
      });
    }, 1000);

    // Redirect to PayPal after 3 seconds
    setTimeout(() => {
      window.open(paypalUrl, '_blank');
      setIsCheckoutOpen(false);
      setCartItems([]);
    }, 3000);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-200">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 sm:h-20">
            <div className="flex items-center gap-2 sm:gap-3">
              <Heart className="h-6 w-6 sm:h-8 sm:w-8 text-blue-600" />
              <div className="flex flex-col">
                <span className="font-bold text-base sm:text-xl text-gray-900">European Peace Tour</span>
                <span className="text-xs text-gray-600">2026</span>
              </div>
            </div>
            
            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-8">
              <button onClick={() => scrollToSection('home')} className="text-gray-700 hover:text-blue-600 transition-colors font-medium">Home</button>
              <button onClick={() => scrollToSection('about')} className="text-gray-700 hover:text-blue-600 transition-colors font-medium">About</button>
              <button onClick={() => scrollToSection('team')} className="text-gray-700 hover:text-blue-600 transition-colors font-medium">Team</button>
              <button onClick={() => scrollToSection('journey')} className="text-gray-700 hover:text-blue-600 transition-colors font-medium">Journey</button>
              <button onClick={() => scrollToSection('faq')} className="text-gray-700 hover:text-blue-600 transition-colors font-medium">FAQ</button>
              <button onClick={() => scrollToSection('support')} className="text-gray-700 hover:text-blue-600 transition-colors font-medium">Support</button>
              <button onClick={() => scrollToSection('merchandise')} className="text-gray-700 hover:text-blue-600 transition-colors font-medium">Shop</button>
              <button onClick={() => scrollToSection('contact')} className="text-gray-700 hover:text-blue-600 transition-colors font-medium">Contact</button>
              {cartItems.length > 0 &&
              <button
                onClick={() => setIsCheckoutOpen(true)}
                className="relative p-2 text-blue-600 hover:text-blue-700 transition-colors">

                  <ShoppingBag className="h-6 w-6" />
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-semibold">
                    {cartItems.length}
                  </span>
                </button>
              }
            </nav>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="md:hidden p-2 text-gray-700 hover:text-blue-600 transition-colors">

              {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>

          {/* Mobile Navigation */}
          {isMenuOpen &&
          <nav className="md:hidden py-4 border-t border-gray-200">
              <div className="flex flex-col gap-3">
                <button onClick={() => scrollToSection('home')} className="text-left px-2 py-2 text-gray-700 hover:text-blue-600 transition-colors font-medium">Home</button>
                <button onClick={() => scrollToSection('about')} className="text-left px-2 py-2 text-gray-700 hover:text-blue-600 transition-colors font-medium">About</button>
                <button onClick={() => scrollToSection('team')} className="text-left px-2 py-2 text-gray-700 hover:text-blue-600 transition-colors font-medium">Team</button>
                <button onClick={() => scrollToSection('journey')} className="text-left px-2 py-2 text-gray-700 hover:text-blue-600 transition-colors font-medium">Journey</button>
                <button onClick={() => scrollToSection('faq')} className="text-left px-2 py-2 text-gray-700 hover:text-blue-600 transition-colors font-medium">FAQ</button>
                <button onClick={() => scrollToSection('support')} className="text-left px-2 py-2 text-gray-700 hover:text-blue-600 transition-colors font-medium">Support</button>
                <button onClick={() => scrollToSection('merchandise')} className="text-left px-2 py-2 text-gray-700 hover:text-blue-600 transition-colors font-medium">Shop</button>
                <button onClick={() => scrollToSection('contact')} className="text-left px-2 py-2 text-gray-700 hover:text-blue-600 transition-colors font-medium">Contact</button>
                {cartItems.length > 0 &&
              <button
                onClick={() => {setIsCheckoutOpen(true);setIsMenuOpen(false);}}
                className="text-left px-2 py-2 text-blue-600 hover:text-blue-700 transition-colors font-medium flex items-center gap-2">

                    <ShoppingBag className="h-5 w-5" />
                    Cart ({cartItems.length})
                  </button>
              }
              </div>
            </nav>
          }
        </div>
      </header>

      {/* Hero Section */}
      <section id="home" className="relative pt-16 sm:pt-20">
        <div className="relative h-[600px] sm:h-[700px] lg:h-[800px] overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://customer-assets.emergentagent.com/job_peacetour-europe/artifacts/j23f437y_reiner%20image.png"
              alt="European Peace Tours - Reiner Kohler"
              className="w-full h-full object-cover object-center" />

            <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/80"></div>
          </div>
          
          <div className="relative container mx-auto px-4 sm:px-6 lg:px-8 h-full flex items-end pb-12 sm:pb-16 lg:pb-20">
            <div className="w-full max-w-4xl mx-auto">
              <div className="bg-black/60 backdrop-blur-xl rounded-2xl p-6 sm:p-8 lg:p-10 border border-white/40 shadow-2xl">
                <h1 className="text-3xl sm:text-4xl lg:text-6xl font-extrabold mb-3 sm:mb-5 leading-tight text-white drop-shadow-[0_4px_12px_rgba(0,0,0,0.9)] text-center">
                  European Peace Tours 2026
                </h1>
                <p className="text-base sm:text-lg lg:text-2xl mb-2 sm:mb-3 font-semibold text-white drop-shadow-[0_2px_8px_rgba(0,0,0,0.9)] text-center">
                  A Motorbike Tour through all Europe to ask all Governments for Peace
                </p>
                <p className="text-sm sm:text-base lg:text-xl mb-5 sm:mb-7 font-medium italic text-yellow-300 drop-shadow-[0_2px_8px_rgba(0,0,0,0.9)] text-center">
                  Eine Motorradtour durch ganz Europa, um alle Regierungen um Frieden zu bitten
                </p>
                <div className="flex justify-center">
                  <Button
                    onClick={() => scrollToSection('contact')}
                    size="lg"
                    className="bg-yellow-400 text-gray-900 hover:bg-yellow-300 px-8 sm:px-10 py-5 sm:py-6 text-base sm:text-lg font-bold transition-all duration-300 hover:scale-105 shadow-xl hover:shadow-2xl">

                    Join the Mission
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-16 sm:py-20 lg:py-24 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center mb-12 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 sm:mb-6 text-gray-900">Goal of the Project</h2>
            <div className="w-20 h-1 bg-blue-600 mx-auto mb-6 sm:mb-8"></div>
            <p className="text-lg sm:text-xl lg:text-2xl text-gray-700 mb-3 sm:mb-4">
              The aim of the tour is to remind people of peace in Europe
            </p>
            <p className="text-base sm:text-lg lg:text-xl text-gray-600 italic">
              Der Sinn ist es die Menschen in Europa an Frieden zu erinnern
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 sm:gap-8 max-w-5xl mx-auto">
            <Card className="border-none shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
              <CardHeader className="text-center pb-4">
                <div className="w-14 h-14 sm:w-16 sm:h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Heart className="h-7 w-7 sm:h-8 sm:w-8 text-blue-600" />
                </div>
                <CardTitle className="text-xl sm:text-2xl">Peace Mission</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-center text-sm sm:text-base">
                  Spreading the message of peace and unity across Europe through direct engagement with governments and communities.
                </CardDescription>
              </CardContent>
            </Card>

            <Card className="border-none shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
              <CardHeader className="text-center pb-4">
                <div className="w-14 h-14 sm:w-16 sm:h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Globe className="h-7 w-7 sm:h-8 sm:w-8 text-green-600" />
                </div>
                <CardTitle className="text-xl sm:text-2xl">European Unity</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-center text-sm sm:text-base">
                  Connecting nations and people through a shared commitment to peace, diplomacy, and mutual understanding.
                </CardDescription>
              </CardContent>
            </Card>

            <Card className="border-none shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
              <CardHeader className="text-center pb-4">
                <div className="w-14 h-14 sm:w-16 sm:h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users className="h-7 w-7 sm:h-8 sm:w-8 text-purple-600" />
                </div>
                <CardTitle className="text-xl sm:text-2xl">Community</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-center text-sm sm:text-base">
                  Building a movement of peace advocates who believe in the power of dialogue and collective action.
                </CardDescription>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Journey Section */}
      <section id="journey" className="py-16 sm:py-20 lg:py-24 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center mb-12 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 sm:mb-6 text-gray-900">Our Journey</h2>
            <div className="w-20 h-1 bg-blue-600 mx-auto mb-6 sm:mb-8"></div>
            <p className="text-base sm:text-lg text-gray-600">
              Follow our detailed route as we travel through Europe, bringing a message of peace to every city
            </p>
          </div>

          {/* Fun Animated Journey Map */}
          <div className="mb-16">
            <FunJourneyMap />
          </div>

          {/* Detailed Stage Information */}
          <div className="max-w-5xl mx-auto">
            <h3 className="text-2xl sm:text-3xl font-bold text-center text-gray-900 mb-8">
              Detailed Route Information
            </h3>
            
            {/* Stage 1 */}
            <div className="relative mb-8 sm:mb-12">
              <div className="bg-gradient-to-br from-blue-50 to-white rounded-xl shadow-lg border-l-4 border-blue-600 p-6 sm:p-8 hover:shadow-xl transition-shadow duration-300">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0 shadow-lg">
                    <span className="text-white font-bold text-lg sm:text-xl">1</span>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">Start: Bundesplatz Bern</h3>
                    <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-sm sm:text-base text-gray-600 mb-3">
                      <span className="font-semibold text-blue-600">📅 26.03.2026</span>
                      <span className="font-semibold text-blue-600">🕐 11:00 AM</span>
                      <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full font-semibold">~285 km</span>
                    </div>
                    <div className="bg-white rounded-lg p-4 border border-blue-100">
                      <p className="text-sm sm:text-base text-gray-700 font-medium mb-2">Route:</p>
                      <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                        <span className="font-semibold text-blue-600">Bundesplatz Bern 🇨🇭</span> → Montreux 🇨🇭 → Martigny 🇨🇭 → Aosta 🇮🇹 → Ivrea 🇮🇹
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Connector */}
              <div className="flex justify-center py-4">
                <div className="w-1 h-8 bg-gradient-to-b from-blue-300 to-green-300"></div>
              </div>
            </div>

            {/* Stage 2 */}
            <div className="relative mb-8 sm:mb-12">
              <div className="bg-gradient-to-br from-green-50 to-white rounded-xl shadow-lg border-l-4 border-green-600 p-6 sm:p-8 hover:shadow-xl transition-shadow duration-300">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 bg-green-600 rounded-full flex items-center justify-center flex-shrink-0 shadow-lg">
                    <span className="text-white font-bold text-lg sm:text-xl">2</span>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">2nd Stage: From Ivrea</h3>
                    <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-sm sm:text-base text-gray-600 mb-3">
                      <span className="font-semibold text-green-600">📅 27.03.2026</span>
                      <span className="font-semibold text-green-600">🕐 9:00 AM</span>
                      <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full font-semibold">~351 km</span>
                    </div>
                    <div className="bg-white rounded-lg p-4 border border-green-100">
                      <p className="text-sm sm:text-base text-gray-700 font-medium mb-2">Route:</p>
                      <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                        <span className="font-semibold text-green-600">Ivrea 🇮🇹</span> → Vercelli → Pavia → Casalpusterlengo → Cremona → Ferrara 🇮🇹
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Connector */}
              <div className="flex justify-center py-4">
                <div className="w-1 h-8 bg-gradient-to-b from-green-300 to-orange-300"></div>
              </div>
            </div>

            {/* Stage 3 */}
            <div className="relative mb-8 sm:mb-12">
              <div className="bg-gradient-to-br from-orange-50 to-white rounded-xl shadow-lg border-l-4 border-orange-600 p-6 sm:p-8 hover:shadow-xl transition-shadow duration-300">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 bg-orange-600 rounded-full flex items-center justify-center flex-shrink-0 shadow-lg">
                    <span className="text-white font-bold text-lg sm:text-xl">3</span>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">3rd Stage: From Ferrara</h3>
                    <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-sm sm:text-base text-gray-600 mb-3">
                      <span className="font-semibold text-orange-600">📅 28.03.2026</span>
                      <span className="font-semibold text-orange-600">🕐 9:00 AM</span>
                      <span className="bg-orange-100 text-orange-800 px-3 py-1 rounded-full font-semibold">~339 km</span>
                    </div>
                    <div className="bg-white rounded-lg p-4 border border-orange-100">
                      <p className="text-sm sm:text-base text-gray-700 font-medium mb-2">Route:</p>
                      <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                        <span className="font-semibold text-orange-600">Ferrara 🇮🇹</span> → Forlì → Arezzo → Grotte di Castro 🇮🇹
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Connector */}
              <div className="flex justify-center py-4">
                <div className="w-1 h-8 bg-gradient-to-b from-orange-300 to-red-300"></div>
              </div>
            </div>

            {/* Stage 4 */}
            <div className="relative mb-8 sm:mb-12">
              <div className="bg-gradient-to-br from-red-50 to-white rounded-xl shadow-lg border-l-4 border-red-600 p-6 sm:p-8 hover:shadow-xl transition-shadow duration-300">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 bg-red-600 rounded-full flex items-center justify-center flex-shrink-0 shadow-lg">
                    <span className="text-white font-bold text-lg sm:text-xl">4</span>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">4th Stage: To Rome</h3>
                    <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-sm sm:text-base text-gray-600 mb-3">
                      <span className="font-semibold text-red-600">📅 29.03.2026</span>
                      <span className="font-semibold text-red-600">🕐 9:00 AM</span>
                    </div>
                    <div className="bg-white rounded-lg p-4 border border-red-100">
                      <p className="text-sm sm:text-base text-gray-700 font-medium mb-2">Destination:</p>
                      <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                        <span className="font-semibold text-red-600">Grotte di Castro 🇮🇹</span> → <span className="text-xl font-bold text-red-600">Rome 🇮🇹</span>
                      </p>
                      <div className="mt-3 p-3 bg-red-50 rounded-lg border border-red-200">
                        <p className="text-sm text-red-800 font-medium">
                          🏛️ Visit to the Vatican - Meeting with the Pope for a blessing on our peace mission
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* To Be Continued */}
            <div className="text-center">
              <div className="inline-block bg-gradient-to-r from-purple-100 to-blue-100 rounded-full px-8 py-4 shadow-lg border-2 border-purple-300">
                <p className="text-xl sm:text-2xl font-bold text-purple-800 mb-1">
                  ...to be continued...
                </p>
                <p className="text-sm text-purple-600">
                  More stages will be announced soon
                </p>
              </div>
            </div>

            {/* Summary Box */}
            <div className="mt-12 bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl shadow-2xl p-6 sm:p-8 text-white">
              <h4 className="text-xl sm:text-2xl font-bold mb-4 text-center">Journey Summary</h4>
              <div className="grid sm:grid-cols-3 gap-4 text-center">
                <div className="bg-white/20 backdrop-blur-sm rounded-lg p-4">
                  <p className="text-2xl sm:text-3xl font-bold mb-1">4+</p>
                  <p className="text-sm sm:text-base">Stages Planned</p>
                </div>
                <div className="bg-white/20 backdrop-blur-sm rounded-lg p-4">
                  <p className="text-2xl sm:text-3xl font-bold mb-1">~975 km</p>
                  <p className="text-sm sm:text-base">Distance So Far</p>
                </div>
                <div className="bg-white/20 backdrop-blur-sm rounded-lg p-4">
                  <p className="text-2xl sm:text-3xl font-bold mb-1">Mar 26</p>
                  <p className="text-sm sm:text-base">Start Date</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Who Are We Section */}
      <section id="team" className="py-16 sm:py-20 lg:py-24 bg-gradient-to-br from-gray-50 via-blue-50 to-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12 sm:mb-16">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 sm:mb-6 text-gray-900">
                Who Are We?
              </h2>
              <div className="w-20 h-1 bg-blue-600 mx-auto mb-6 sm:mb-8"></div>
              <p className="text-base sm:text-lg text-gray-600">
                Meet the dedicated team behind the European Peace Tours
              </p>
            </div>

            <div className="space-y-8 sm:space-y-12">
              {/* Reiner Kohler - Initiator */}
              <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-200 hover:shadow-2xl transition-all duration-300">
                <div className="md:flex">
                  <div className="md:w-1/3 bg-gradient-to-br from-blue-600 to-blue-700 p-8 flex flex-col items-center justify-center text-white">
                    <div className="w-40 h-40 sm:w-48 sm:h-48 rounded-full overflow-hidden border-4 border-white shadow-2xl mb-4">
                      <img
                        src="https://customer-assets.emergentagent.com/job_peacetour-europe/artifacts/9xusixhp_image.png"
                        alt="Reiner Kohler"
                        className="w-full h-full object-cover" />

                    </div>
                    <h3 className="text-2xl sm:text-3xl font-bold mb-2">Reiner Kohler</h3>
                    <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full">
                      <span className="text-sm sm:text-base font-semibold">Initiator</span>
                    </div>
                  </div>
                  
                  <div className="md:w-2/3 p-6 sm:p-8">
                    <div className="prose prose-lg max-w-none">
                      <p className="text-base sm:text-lg text-gray-700 leading-relaxed mb-4">
                        My name is <strong className="text-blue-600">Reiner Kohler</strong>. I was born and raised in Germany. The love of my life brought me to Switzerland, where I have now lived for almost forty years.
                      </p>
                      <p className="text-base sm:text-lg text-gray-700 leading-relaxed mb-4">
                        Like many of my fellow human beings, I was fortunate enough to grow up and live in a peaceful time. I would like to pass this achievement on to my children and grandchildren.
                      </p>
                      <p className="text-base sm:text-lg text-gray-700 leading-relaxed">
                        Therefore, in my own way, I am committed to peace and economic cooperation in Europe.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Jo Künzle - Finances */}
              <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-200 hover:shadow-2xl transition-all duration-300">
                <div className="md:flex md:flex-row-reverse">
                  <div className="md:w-1/3 bg-gradient-to-br from-green-600 to-green-700 p-8 flex flex-col items-center justify-center text-white">
                    <div className="w-40 h-40 sm:w-48 sm:h-48 rounded-full overflow-hidden border-4 border-white shadow-2xl mb-4">
                      <img
                        src="https://customer-assets.emergentagent.com/job_peacetour-europe/artifacts/x0lzx3ja_image.png"
                        alt="Jo Künzle"
                        className="w-full h-full object-cover" />

                    </div>
                    <h3 className="text-2xl sm:text-3xl font-bold mb-2">Jo Künzle</h3>
                    <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full mb-4">
                      <span className="text-sm sm:text-base font-semibold">Finances</span>
                    </div>
                    <a
                      href="https://artetreuhand.ch"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center hover:bg-green-50 transition-colors duration-300 hover:shadow-lg !font-semibold gap-2 px-5 py-2.5 rounded-lg shadow-md text-green-700 bg-white">

                      arte Treuhand
                      <ExternalLink className="h-4 w-4" />
                    </a>
                  </div>
                  
                  <div className="md:w-2/3 p-6 sm:p-8 flex items-center">
                    <div>
                      <p className="text-base sm:text-lg text-gray-700 leading-relaxed mb-4">
                        <strong className="text-green-600">Jo Künzle</strong> handles all financial matters for the European Peace Tours, ensuring transparency and proper management of donations and resources.
                      </p>
                      <p className="text-base sm:text-lg text-gray-700 leading-relaxed">
                        With professional expertise in financial management, Jo ensures that every contribution directly supports our peace mission across Europe.
                      </p>
                      <div className="mt-6 p-4 bg-green-50 border-l-4 border-green-600 rounded-r-lg">
                        <p className="text-sm sm:text-base text-green-800 font-medium">
                          For financial inquiries and transparency reports, visit Arte Treuhand
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Team Call to Action */}
            <div className="mt-12 text-center bg-blue-600 rounded-xl p-6 sm:p-8 shadow-xl">
              <h3 className="text-2xl sm:text-3xl font-bold text-white mb-3">Join Our Mission</h3>
              <p className="text-base sm:text-lg text-blue-100 mb-4">
                Together, we're building a movement for peace across Europe
              </p>
              <Button
                onClick={() => scrollToSection('support')}
                className="bg-white text-blue-600 hover:bg-blue-50 px-8 py-6 text-lg font-semibold transition-all duration-300 hover:scale-105 shadow-lg">

                Support Our Cause
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* What Drives Us Section */}
      <section id="why" className="py-16 sm:py-20 lg:py-24 bg-gradient-to-b from-gray-900 to-gray-800 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmZmZmYiIGZpbGwtb3BhY2l0eT0iMC40Ij48cGF0aCBkPSJNMzYgMzRjMC0yLjIxLTEuNzktNC00LTRzLTQgMS43OS00IDQgMS43OSA0IDQgNCA0LTEuNzkgNC00em0wLTEwYzAtMi4yMS0xLjc5LTQtNC00cy00IDEuNzktNCA0IDEuNzkgNCA0IDQgNC0xLjc5IDQtNHptMC0xMGMwLTIuMjEtMS43OS00LTQtNHMtNCAxLjc5LTQgNCAxLjc5IDQgNCA0IDQtMS43OSA0LTR6Ii8+PC9nPjwvZz48L3N2Zz4=')]"></div>
        </div>
        
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="max-w-4xl mx-auto">
            {/* Header */}
            <div className="text-center mb-12 sm:mb-16">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 sm:mb-6">
                What Drives Us?
              </h2>
              <div className="w-20 h-1 bg-red-500 mx-auto mb-6 sm:mb-8"></div>
            </div>

            {/* Bilingual Text Content */}
            <div className="mb-12 sm:mb-16 space-y-6">
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 sm:p-8 border border-white/20">
                <p className="text-base sm:text-lg leading-relaxed mb-4">
                  Current events, both private and social, are becoming increasingly harsh. Political decisions are increasingly adapted to language, and those with different opinions are excluded from dialogue. Greed and lust for power are exerting a significant influence on events.
                </p>
                <p className="text-xl sm:text-2xl font-bold text-red-400 mt-6">
                  But haven't we had enough wars in Europe?
                </p>
              </div>

              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 sm:p-8 border border-white/20">
                <p className="text-base sm:text-lg leading-relaxed mb-4 italic">
                  Die allgemeinen Zeitgeschehen, die private und gesellschaftliche Rhetorik wird immer rauher. Politische Entscheidungen werden immer mehr der Sprache angepasst und Andersdenkende vom Dialog ausgeschlossen. Geldgier und Machtgier nehmen erheblichen Einfluss auf das Geschehen.
                </p>
                <p className="text-xl sm:text-2xl font-bold text-red-400 mt-6 italic">
                  Aber, hatten wir nicht in Europa genug Kriege?
                </p>
              </div>
            </div>

            {/* Why Are We Doing This */}
            <div className="text-center mb-8 sm:mb-12">
              <h3 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-4">
                Why Are We Doing This?
              </h3>
              <div className="w-16 h-1 bg-red-500 mx-auto"></div>
            </div>

            {/* Historical Images with Captions */}
            <div className="grid md:grid-cols-2 gap-8 sm:gap-12 mb-12">
              {/* First Image */}
              <div className="group">
                <div className="relative overflow-hidden rounded-lg shadow-2xl mb-4 border-4 border-white/20">
                  <img
                    src="https://customer-assets.emergentagent.com/job_peacetour-europe/artifacts/rfx1gaqz_image.png"
                    alt="War victim"
                    className="w-full h-64 sm:h-80 object-cover grayscale hover:grayscale-0 transition-all duration-700" />

                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                </div>
                <p className="text-center text-lg sm:text-xl font-semibold text-red-400 leading-relaxed">
                  Was this your Aunt, Cousin, Brother or Daughter?
                </p>
              </div>

              {/* Second Image */}
              <div className="group">
                <div className="relative overflow-hidden rounded-lg shadow-2xl mb-4 border-4 border-white/20">
                  <img
                    src="https://customer-assets.emergentagent.com/job_peacetour-europe/artifacts/5oon0rx5_image.png"
                    alt="Young soldiers"
                    className="w-full h-64 sm:h-80 object-cover grayscale hover:grayscale-0 transition-all duration-700" />

                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                </div>
                <p className="text-center text-lg sm:text-xl font-semibold text-red-400 leading-relaxed">
                  Killed Enemies? ...No, young men, which have not even started their lives
                </p>
              </div>
            </div>

            {/* Call to Action */}
            <div className="text-center bg-red-600/20 backdrop-blur-sm rounded-xl p-6 sm:p-8 border-2 border-red-500/50">
              <p className="text-xl sm:text-2xl font-bold mb-4">
                This is why we ride for peace.
              </p>
              <p className="text-base sm:text-lg text-gray-200">
                To remind Europe that dialogue, not division, is the path forward. To honor those who were lost and protect those who are yet to come.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Support Section */}
      <section id="support" className="py-16 sm:py-20 lg:py-24 bg-white relative overflow-hidden">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="max-w-3xl mx-auto text-center mb-12 sm:mb-16">
            <div className="inline-flex items-center gap-2 bg-green-100 text-green-700 px-4 py-2 rounded-full text-sm font-semibold mb-4">
              <HandHeart className="h-4 w-4" />
              Make a Difference
            </div>
            <h2 className="sm:text-4xl lg:text-5xl sm:mb-6 !font-bold !text-3xl mb-4 text-gray-900">Your Support in Needed

            </h2>
            <div className="w-20 h-1 bg-blue-600 mx-auto mb-6 sm:mb-8"></div>
            <p className="text-base sm:text-lg text-gray-600">
              Every contribution helps us spread the message of peace across Europe. Choose how you'd like to support our mission.
            </p>
          </div>

          {/* Support Options Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8 max-w-6xl mx-auto mb-12">
            {/* Donate */}
            <Card className="border-2 border-green-200 hover:border-green-400 transition-all duration-300 hover:-translate-y-2 hover:shadow-xl bg-gradient-to-br from-green-50 to-white">
              <CardHeader className="text-center pb-4">
                <div className="w-14 h-14 sm:w-16 sm:h-16 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                  <DollarSign className="h-7 w-7 sm:h-8 sm:w-8 text-white" />
                </div>
                <CardTitle className="text-xl sm:text-2xl text-gray-900">Donate</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-center text-sm sm:text-base text-gray-700">
                  Donate a small amount to our peace tour by sending a Penny by PayPal. Every contribution counts!
                </CardDescription>
              </CardContent>
            </Card>

            {/* Join on Motorcycle */}
            <Card className="border-2 border-blue-200 hover:border-blue-400 transition-all duration-300 hover:-translate-y-2 hover:shadow-xl bg-gradient-to-br from-blue-50 to-white">
              <CardHeader className="text-center pb-4">
                <div className="w-14 h-14 sm:w-16 sm:h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                  <Bike className="h-7 w-7 sm:h-8 sm:w-8 text-white" />
                </div>
                <CardTitle className="text-xl sm:text-2xl text-gray-900">Join Us</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-center text-sm sm:text-base text-gray-700">
                  Join us on your motorcycle, as far and as long as you like. Ride with us for peace!
                </CardDescription>
              </CardContent>
            </Card>

            {/* Wave & Support */}
            <Card className="border-2 border-purple-200 hover:border-purple-400 transition-all duration-300 hover:-translate-y-2 hover:shadow-xl bg-gradient-to-br from-purple-50 to-white">
              <CardHeader className="text-center pb-4">
                <div className="w-14 h-14 sm:w-16 sm:h-16 bg-purple-600 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                  <Users className="h-7 w-7 sm:h-8 sm:w-8 text-white" />
                </div>
                <CardTitle className="text-xl sm:text-2xl text-gray-900">Wave to Us</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-center text-sm sm:text-base text-gray-700">
                  No motorcycle? Wave to us and make noise when we pass by. Your cheer matters!
                </CardDescription>
              </CardContent>
            </Card>

            {/* Sign Petition */}
            <Card className="border-2 border-orange-200 hover:border-orange-400 transition-all duration-300 hover:-translate-y-2 hover:shadow-xl bg-gradient-to-br from-orange-50 to-white">
              <CardHeader className="text-center pb-4">
                <div className="w-14 h-14 sm:w-16 sm:h-16 bg-orange-600 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                  <FileSignature className="h-7 w-7 sm:h-8 sm:w-8 text-white" />
                </div>
                <CardTitle className="text-xl sm:text-2xl text-gray-900">Sign Petition</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-center text-sm sm:text-base text-gray-700">
                  Join our petition and sign it with your name. Add your voice to the peace movement!
                </CardDescription>
              </CardContent>
            </Card>
          </div>

          {/* Donation Methods */}
          <div className="max-w-4xl mx-auto">
            <div className="bg-gradient-to-br from-blue-50 via-white to-green-50 rounded-2xl shadow-2xl border-2 border-blue-200 p-6 sm:p-8 lg:p-10">
              <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6 text-center flex items-center justify-center gap-3">
                <Heart className="h-8 w-8 text-red-500" />
                Ways to Contribute
              </h3>
              
              <div className="grid md:grid-cols-2 gap-6 sm:gap-8">
                {/* Financial Support */}
                <div className="space-y-4">
                  <h4 className="text-lg font-semibold text-gray-900 border-b-2 border-blue-300 pb-2 mb-4">
                    Financial Support
                  </h4>
                  
                  {/* Bank Account */}
                  <div className="bg-white rounded-lg p-4 shadow-md border border-gray-200 hover:shadow-lg transition-shadow">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <DollarSign className="h-5 w-5 text-blue-600" />
                      </div>
                      <div className="flex-1">
                        <p className="font-semibold text-gray-900 mb-1">Bank Transfer (UBS)</p>
                        <p className="text-sm text-gray-600 font-mono break-all mb-1">CH08 0023 3233 2207 7340 D</p>
                        <p className="text-xs text-gray-500">DREAMWORKS Culture Support</p>
                        <p className="text-xs text-blue-600 font-semibold mt-1">EPT-Donation</p>
                      </div>
                    </div>
                  </div>

                  {/* PayPal */}
                  <div className="bg-white rounded-lg p-4 shadow-md border border-gray-200 hover:shadow-lg transition-shadow">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <HandHeart className="h-5 w-5 text-blue-600" />
                      </div>
                      <div className="flex-1">
                        <p className="font-semibold text-gray-900 mb-2">PayPal</p>
                        <a
                          href="https://paypal.me/EuropeanPeaceTours"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700 font-medium hover:underline">

                          paypal.me/EuropeanPeaceTours
                          <ExternalLink className="h-4 w-4" />
                        </a>
                      </div>
                    </div>
                  </div>

                  {/* Crowdfunding */}
                  <div className="bg-white rounded-lg p-4 shadow-md border border-gray-200 hover:shadow-lg transition-shadow">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Users className="h-5 w-5 text-green-600" />
                      </div>
                      <div className="flex-1">
                        <p className="font-semibold text-gray-900 mb-2">GoFundMe Campaign</p>
                        <a
                          href="https://gofund.me/b28073e11"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 text-sm text-green-600 hover:text-green-700 font-medium hover:underline">

                          Support on GoFundMe
                          <ExternalLink className="h-4 w-4" />
                        </a>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Take Action */}
                <div className="space-y-4">
                  <h4 className="text-lg font-semibold text-gray-900 border-b-2 border-green-300 pb-2 mb-4">
                    Take Action
                  </h4>
                  
                  {/* Petition */}
                  <div className="bg-white rounded-lg p-6 shadow-md border border-gray-200 hover:shadow-lg transition-shadow">
                    <div className="flex items-start gap-3 mb-4">
                      <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <FileSignature className="h-5 w-5 text-orange-600" />
                      </div>
                      <div className="flex-1">
                        <p className="font-semibold text-gray-900 mb-1">Sign the Petition</p>
                        <p className="text-sm text-gray-600 mb-3">Add your voice to thousands supporting peace in Europe</p>
                      </div>
                    </div>
                    <a
                      href="https://www.openpetition.de/petition/online/european-peace-tour"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-full inline-flex items-center justify-center gap-2 bg-orange-600 hover:bg-orange-700 text-white px-6 py-3 rounded-lg font-semibold transition-all duration-300 hover:scale-105 shadow-md">

                      Sign Now
                      <ExternalLink className="h-5 w-5" />
                    </a>
                  </div>

                  {/* Quick Info */}
                  <div className="bg-blue-50 rounded-lg p-4 border-l-4 border-blue-600">
                    <p className="text-sm text-gray-700 leading-relaxed">
                      <strong className="text-blue-900">Every action matters!</strong> Whether you donate a penny, sign our petition, or simply wave as we pass by, you're contributing to a powerful message of peace across Europe.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 sm:py-16 bg-blue-600 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmZmZmYiIGZpbGwtb3BhY2l0eT0iMC40Ij48cGF0aCBkPSJNMzYgMzRjMC0yLjIxLTEuNzktNC00LTRzLTQgMS43OS00IDQgMS43OSA0IDQgNCA0LTEuNzkgNC00em0wLTEwYzAtMi4yMS0xLjc5LTQtNC00cy00IDEuNzktNCA0IDEuNzkgNCA0IDQgNC0xLjc5IDQtNHptMC0xMGMwLTIuMjEtMS43OS00LTQtNHMtNCAxLjc5LTQgNCAxLjc5IDQgNCA0IDQtMS43OSA0LTR6Ii8+PC9nPjwvZz48L3N2Zz4=')]"></div>
        </div>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 sm:gap-8">
            <div className="text-center">
              <div className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-2">27+</div>
              <div className="text-sm sm:text-base text-blue-100">European Capitals</div>
            </div>
            <div className="text-center">
              <div className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-2">2026</div>
              <div className="text-sm sm:text-base text-blue-100">Year of Peace</div>
            </div>
            <div className="text-center">
              <div className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-2">1</div>
              <div className="text-sm sm:text-base text-blue-100">United Mission</div>
            </div>
            <div className="text-center">
              <div className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-2">∞</div>
              <div className="text-sm sm:text-base text-blue-100">Peace Forever</div>
            </div>
          </div>
        </div>
      </section>

      {/* Merchandise Section */}
      <section id="merchandise" className="py-16 sm:py-20 lg:py-24 bg-gradient-to-b from-gray-50 to-white relative">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1636690513351-0af1763f6237?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwxfHxwcmVtaXVtJTIwYmFja2dyb3VuZHxlbnwwfHx8fDE3Njc4MjE3MDR8MA&ixlib=rb-4.1.0&q=85')] bg-cover bg-center opacity-5"></div>
        
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="max-w-3xl mx-auto text-center mb-12 sm:mb-16">
            <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-700 px-4 py-2 rounded-full text-sm font-semibold mb-4">
              <Sparkles className="h-4 w-4" />
              Support the Mission
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 sm:mb-6 text-gray-900">
              Official Merchandise
            </h2>
            <div className="w-20 h-1 bg-blue-600 mx-auto mb-6 sm:mb-8"></div>
            <p className="text-base sm:text-lg text-gray-600">
              Get your exclusive peace tour merchandise and support our mission to promote peace across Europe
            </p>
          </div>

          {/* Feature Badges */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6 max-w-4xl mx-auto mb-12">
            <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-100 flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                <Award className="h-5 w-5 text-blue-600" />
              </div>
              <div className="text-left">
                <div className="font-semibold text-gray-900 text-sm">Premium Quality</div>
                <div className="text-xs text-gray-600">High-quality materials</div>
              </div>
            </div>
            <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-100 flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                <Heart className="h-5 w-5 text-green-600" />
              </div>
              <div className="text-left">
                <div className="font-semibold text-gray-900 text-sm">Support Peace</div>
                <div className="text-xs text-gray-600">100% for the mission</div>
              </div>
            </div>
            <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-100 flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                <Package className="h-5 w-5 text-purple-600" />
              </div>
              <div className="text-left">
                <div className="font-semibold text-gray-900 text-sm">Fast Shipping</div>
                <div className="text-xs text-gray-600">Delivered to your door</div>
              </div>
            </div>
          </div>

          {/* Merchandise Grid */}
          <div className="grid md:grid-cols-2 gap-8 sm:gap-12 max-w-5xl mx-auto">
            {merchandiseItems.map((item) =>
            <MerchandiseCard
              key={item.id}
              item={item}
              onAddToCart={handleAddToCart} />

            )}
          </div>

          {/* Payment Info Banner */}
          <div className="max-w-3xl mx-auto mt-12 bg-white border-2 border-blue-200 rounded-xl p-6 sm:p-8 shadow-lg">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                <ShoppingBag className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">How to Order</h3>
                <p className="text-gray-600 text-sm sm:text-base mb-3">
                  All purchases are processed through PayPal for secure transactions. After adding items to your cart:
                </p>
                <ol className="list-decimal list-inside space-y-2 text-sm sm:text-base text-gray-700">
                  <li>Complete the checkout form with your shipping details</li>
                  <li>Make your donation via PayPal to: <strong className="text-blue-600">iscream@hotmail.ch</strong></li>
                  <li>Include your order details, size (for T-shirts), name and address in the PayPal comment</li>
                  <li>Receive your merchandise by mail within 2-3 weeks</li>
                </ol>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-16 sm:py-20 lg:py-24 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12 sm:mb-16">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 sm:mb-6 text-gray-900">
                Frequently Asked Questions
              </h2>
              <div className="w-20 h-1 bg-blue-600 mx-auto mb-6 sm:mb-8"></div>
              <p className="text-base sm:text-lg text-gray-600">
                Find answers to common questions about the European Peace Tours
              </p>
            </div>

            <div className="space-y-4">
              {/* Question 1 */}
              <div className="bg-gradient-to-br from-blue-50 to-white rounded-xl shadow-lg border border-blue-200 overflow-hidden">
                <div className="p-6 sm:p-8">
                  <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 flex items-start gap-3">
                    <span className="flex-shrink-0 w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold">
                      1
                    </span>
                    Why are you doing this?
                  </h3>
                  <div className="ml-11 text-base sm:text-lg text-gray-700 leading-relaxed">
                    <p>
                      I want to preserve peace among the European peoples. Because every individual has the right to a free and peaceful life.
                    </p>
                  </div>
                </div>
              </div>

              {/* Question 2 */}
              <div className="bg-gradient-to-br from-green-50 to-white rounded-xl shadow-lg border border-green-200 overflow-hidden">
                <div className="p-6 sm:p-8">
                  <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 flex items-start gap-3">
                    <span className="flex-shrink-0 w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center text-sm font-bold">
                      2
                    </span>
                    What are you telling the Governments?
                  </h3>
                  <div className="ml-11 text-base sm:text-lg text-gray-700 leading-relaxed">
                    <p>
                      I would like to ask those in power to personally commit to peace and create opportunities for dialogue. <strong className="text-green-700">Words are the solution, not weapons.</strong>
                    </p>
                  </div>
                </div>
              </div>

              {/* Question 3 */}
              <div className="bg-gradient-to-br from-purple-50 to-white rounded-xl shadow-lg border border-purple-200 overflow-hidden">
                <div className="p-6 sm:p-8">
                  <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 flex items-start gap-3">
                    <span className="flex-shrink-0 w-8 h-8 bg-purple-600 text-white rounded-full flex items-center justify-center text-sm font-bold">
                      3
                    </span>
                    How can I help?
                  </h3>
                  <div className="ml-11 space-y-3 text-base sm:text-lg text-gray-700 leading-relaxed">
                    <p className="flex items-start gap-2">
                      <span className="text-purple-600 font-bold mt-1">•</span>
                      <span>You can join me on the trip. As much, as long, as often as you like. The route and tour dates will be published.</span>
                    </p>
                    <p className="flex items-start gap-2">
                      <span className="text-purple-600 font-bold mt-1">•</span>
                      <span>You can send me some pocket money; you can find the links under <button onClick={() => scrollToSection('support')} className="text-purple-600 font-semibold hover:underline">"Your Support."</button></span>
                    </p>
                    <p className="flex items-start gap-2">
                      <span className="text-purple-600 font-bold mt-1">•</span>
                      <span>You can buy some merchandise – also under <button onClick={() => scrollToSection('merchandise')} className="text-purple-600 font-semibold hover:underline">"Your Support."</button></span>
                    </p>
                    <p className="flex items-start gap-2">
                      <span className="text-purple-600 font-bold mt-1">•</span>
                      <span>If we have sponsors, check out their websites. They all have very interesting offers – see <button onClick={() => scrollToSection('sponsors')} className="text-purple-600 font-semibold hover:underline">"Sponsors."</button></span>
                    </p>
                  </div>
                </div>
              </div>

              {/* Question 4 */}
              <div className="bg-gradient-to-br from-orange-50 to-white rounded-xl shadow-lg border border-orange-200 overflow-hidden">
                <div className="p-6 sm:p-8">
                  <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 flex items-start gap-3">
                    <span className="flex-shrink-0 w-8 h-8 bg-orange-600 text-white rounded-full flex items-center justify-center text-sm font-bold">
                      4
                    </span>
                    Do you do this professionally?
                  </h3>
                  <div className="ml-11 space-y-3 text-base sm:text-lg text-gray-700 leading-relaxed">
                    <p>
                      <strong className="text-orange-700">No. It's a one-off.</strong> But I want to convince as many peace-loving people as possible to actively work for peace, no matter where they live or what their status is.
                    </p>
                    <p className="text-gray-600 italic">
                      If there are any surpluses from the fundraising, they will be used for various social projects in the respective countries.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Additional Questions CTA */}
            <div className="mt-12 text-center bg-gradient-to-r from-blue-100 to-purple-100 rounded-xl p-6 sm:p-8 border-2 border-blue-300">
              <h4 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3">Still have questions?</h4>
              <p className="text-base sm:text-lg text-gray-700 mb-4">
                Feel free to reach out to us directly
              </p>
              <Button
                onClick={() => scrollToSection('contact')}
                className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-6 text-lg font-semibold transition-all duration-300 hover:scale-105 shadow-lg">

                Contact Us
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Our Sponsors Section */}
      <section id="sponsors" className="py-16 sm:py-20 lg:py-24 bg-gradient-to-br from-purple-50 via-white to-blue-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12 sm:mb-16">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 sm:mb-6 text-gray-900">
                Our Sponsors
              </h2>
              <div className="w-20 h-1 bg-purple-600 mx-auto mb-6 sm:mb-8"></div>
            </div>

            {/* Sponsor Message */}
            <div className="mb-12 sm:mb-16">
              <div className="bg-white rounded-2xl shadow-xl p-6 sm:p-8 lg:p-10 border-2 border-purple-200">
                <div className="flex items-start gap-4 mb-6">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 bg-purple-600 rounded-full flex items-center justify-center flex-shrink-0">
                    <Heart className="h-6 w-6 sm:h-7 sm:w-7 text-white" />
                  </div>
                  <div className="flex-1">
                    <p className="text-lg sm:text-xl text-gray-700 leading-relaxed mb-4">
                      <span className="font-bold text-purple-600">"Money makes the World go around"</span>...that's how it is with us, too.
                    </p>
                  </div>
                </div>
                
                <div className="space-y-4 text-base sm:text-lg text-gray-700 leading-relaxed">
                  <p>
                    We want to pay our fellow campaigners and helpers fairly for their work. But travel expenses like fuel, equipment, repairs, and food also cost a lot of money. And we also need somewhere to sleep.
                  </p>
                  <p className="font-semibold text-purple-800">
                    Our sponsors are committed to this. Please visit the websites of our sponsors listed below. You'll find many great offers for yourself.
                  </p>
                </div>
              </div>
            </div>

            {/* Thanks To Section */}
            <div className="bg-gradient-to-br from-purple-600 to-blue-600 rounded-2xl shadow-2xl p-8 sm:p-10 lg:p-12 text-white">
              <h3 className="text-2xl sm:text-3xl font-bold mb-8 text-center flex items-center justify-center gap-3">
                <Award className="h-8 w-8" />
                Thanks To
              </h3>
              
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
                {[
                'Andrea Wüst',
                'Jaqueline Bestgen',
                'Andrew Padre-Fernandez',
                'Remo Jenni',
                'Olivier Burnand',
                'Ivan Kastschmarov',
                'Stefania Iliu Ion',
                'Christoph Kohler'].
                map((sponsor, index) =>
                <div
                  key={index}
                  className="bg-white/20 backdrop-blur-sm rounded-lg p-4 text-center hover:bg-white/30 transition-all duration-300 hover:scale-105 border border-white/30">

                    <p className="font-semibold text-base sm:text-lg text-white">
                      {sponsor}
                    </p>
                  </div>
                )}
              </div>

              <div className="mt-8 text-center">
                <p className="text-lg sm:text-xl font-semibold text-blue-100">
                  Thank you for believing in our mission!
                </p>
              </div>
            </div>

            {/* Become a Sponsor CTA */}
            <div className="mt-12 text-center bg-white rounded-xl shadow-lg p-6 sm:p-8 border-2 border-purple-200">
              <h4 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3">Want to Become a Sponsor?</h4>
              <p className="text-base sm:text-lg text-gray-600 mb-6">
                Support the European Peace Tours and help us spread peace across the continent
              </p>
              <Button
                onClick={() => scrollToSection('contact')}
                className="bg-purple-600 hover:bg-purple-700 text-white px-8 py-6 text-lg font-semibold transition-all duration-300 hover:scale-105 shadow-lg">

                Get in Touch
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-16 sm:py-20 lg:py-24 bg-gray-50 relative">
        <div className="absolute inset-0 opacity-5">
          <img
            src="https://images.pexels.com/photos/35246241/pexels-photo-35246241.jpeg"
            alt="Background"
            className="w-full h-full object-cover" />

        </div>
        
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="max-w-3xl mx-auto text-center mb-12 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 sm:mb-6 text-gray-900">Participate?</h2>
            <div className="w-20 h-1 bg-blue-600 mx-auto mb-6 sm:mb-8"></div>
            <p className="text-base sm:text-lg text-gray-700 mb-2">Wanna know more? Write an E-Mail to:</p>
            <p className="text-base sm:text-lg text-gray-700 italic">Sie möchten mehr über das Projekt erfahren? Schreiben Sie einfach eine E-Mail:</p>
            <a href="mailto:iscream@hotmail.ch" className="text-lg sm:text-xl font-semibold text-blue-600 hover:text-blue-700 mt-4 inline-block transition-colors">
              iscream@hotmail.ch
            </a>
          </div>

          <div className="max-w-2xl mx-auto">
            <Card className="border-none shadow-xl">
              <CardHeader>
                <CardTitle className="text-2xl sm:text-3xl text-center">Get in Touch</CardTitle>
                <CardDescription className="text-center text-sm sm:text-base">
                  Send us a message and we'll get back to you as soon as possible
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">Name</label>
                    <Input
                      id="name"
                      name="name"
                      type="text"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Your name"
                      className="w-full" />

                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="your.email@example.com"
                      className="w-full" />

                  </div>
                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">Message</label>
                    <Textarea
                      id="message"
                      name="message"
                      required
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Tell us about your interest in the peace tour..."
                      rows={5}
                      className="w-full" />

                  </div>
                  <Button
                    type="submit"
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white py-5 sm:py-6 text-base sm:text-lg font-semibold transition-all duration-300 hover:scale-[1.02]">

                    <Mail className="mr-2 h-5 w-5" />
                    Send Message
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Imprint Section */}
      <section id="imprint" className="py-16 sm:py-20 bg-gray-100">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-10 sm:mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold mb-3 text-gray-900">
                Imprint (Impressum)
              </h2>
              <div className="w-20 h-1 bg-gray-600 mx-auto"></div>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-6 sm:p-8 lg:p-10 border border-gray-200">
              {/* Organization Info */}
              <div className="mb-8 pb-8 border-b border-gray-200">
                <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4">
                  DREAMWORKS Culture Support
                </h3>
                <p className="text-base sm:text-lg text-gray-700 mb-4">
                  A registered, non-profit association under Swiss law.
                </p>
              </div>

              {/* Contact Person */}
              <div className="mb-8 pb-8 border-b border-gray-200">
                <h4 className="text-lg sm:text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Users className="h-5 w-5 text-blue-600" />
                  Contact Person
                </h4>
                <div className="space-y-2 text-base sm:text-lg text-gray-700">
                  <p className="font-semibold text-gray-900">Reiner Kohler, President</p>
                  <p>Hauptstrasse 20</p>
                  <p>CH-4147 Aesch</p>
                  <p>Switzerland</p>
                  <p className="mt-3 flex items-center gap-2">
                    <Mail className="h-5 w-5 text-blue-600" />
                    <a
                      href="mailto:iscream@hotmail.ch"
                      className="text-blue-600 hover:text-blue-700 hover:underline font-medium">

                      iscream@hotmail.ch
                    </a>
                  </p>
                </div>
              </div>

              {/* Legal Notice */}
              <div>
                <h4 className="text-lg sm:text-xl font-semibold text-gray-900 mb-4">
                  Legal Notice
                </h4>
                <div className="space-y-4 text-sm sm:text-base text-gray-700 leading-relaxed">
                  <p>
                    All images and elements used on this website are either freely available on the internet under license-free terms or are original creations. There is no intention to infringe upon or misuse the rights of third parties. Should any such infringement nevertheless occur, it is unintentional.
                  </p>
                  <p>
                    Upon becoming aware of any legal infringement, whether through our own review or upon notification by a rights holder, the respective content will be corrected or removed without delay.
                  </p>
                  <p>
                    We will not enter into any claims that are unlawful or include demands for financial compensation.
                  </p>
                  <p className="font-semibold text-gray-900">
                    If you identify any legal violations, please contact us using the contact details provided in this imprint.
                  </p>
                  <div className="mt-6 p-4 bg-blue-50 border-l-4 border-blue-600 rounded-r-lg">
                    <p className="text-sm sm:text-base text-blue-900 font-medium">
                      <strong>Copyright Notice:</strong> The logo displayed on this website is protected by copyright and may only be used with our explicit consent.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-8 sm:py-12">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6 mb-6">
            <div className="flex items-center gap-3">
              <Heart className="h-8 w-8 text-blue-400" />
              <div className="flex flex-col">
                <span className="font-bold text-lg sm:text-xl">European Peace Tour 2026</span>
                <span className="text-sm text-gray-400">Riding for Peace across Europe</span>
              </div>
            </div>
            <div className="text-center md:text-right">
              <p className="text-sm text-gray-400">Contact: <a href="mailto:iscream@hotmail.ch" className="text-blue-400 hover:text-blue-300 transition-colors">iscream@hotmail.ch</a></p>
              <p className="text-sm text-gray-500 mt-2">© 2026 European Peace Tours. All rights reserved.</p>
              <button
                onClick={() => scrollToSection('imprint')}
                className="text-sm text-gray-400 hover:text-blue-400 transition-colors mt-2 underline">

                Imprint (Impressum)
              </button>
            </div>
          </div>
          
          {/* Website Designer Credits */}
          <div className="border-t border-gray-800 pt-6 text-center">
            <p className="text-sm text-gray-400">
              Website Design: <span className="text-blue-400 font-semibold">Kesani Krishna Sri Chaitanya</span>
            </p>
            <p className="text-xs text-gray-500 mt-1">
              Need a professional website? Contact: <a href="mailto:k.kesani@stud.unibas.ch" className="text-blue-400 hover:text-blue-300 transition-colors">k.kesani@stud.unibas.ch</a>
            </p>
          </div>
        </div>
      </footer>

      {/* Checkout Modal */}
      <CheckoutModal
        isOpen={isCheckoutOpen}
        onClose={() => setIsCheckoutOpen(false)}
        cartItems={cartItems}
        onCheckout={handleCheckout} />

    </div>);

};

export default Home;